import { Link } from "react-router-dom-v5-compat";
import { IconNameEnum, useModuleBEM } from "@b2bportal/core-themes";
import { TrackedLoadedPageName } from "@b2bportal/core-tracking";
import { CoreUiComponents, type Funnel } from "@b2bportal/core-types";
import { useExperiment } from "@hopper-b2b/experiments";
import { Trans, useI18nContext } from "@hopper-b2b/i18n";
import { Icon, Page, PageHeader } from "@tenant/ui";
import {
  CombinedSearchBar,
  HomepagePerksSection,
  UpcomingTripsSection,
  PopularCategoriesSection,
  RecentSearchesSection,
} from "./components";
import { CarsSection } from "./components/CarsSection";
import { useCarsSectionBanners } from "./components/CarsSection/hooks";
import { CompleteYourTripSection } from "./components/CompleteYourTripSection";
import { ExploreFurtherSection } from "./components/ExploreFurtherSection";
import { useExploreFurtherBanners } from "./components/ExploreFurtherSection/hooks";
import { MoreTravelSavingsSection } from "./components/MoreTravelSavingsSection";
import { Section, SectionTitle } from "./components/Section";
import styles from "./Homepage.module.scss";
import { HOMEPAGE_SNAP_SECTIONS } from "./utils";
import { ActionButton } from "@/components/ActionButton";
import { SignInBanner } from "@/components/SignInBanner";
import { useShowCarsSearch } from "@/hooks/useShowCarsSearch";
import { FAQ_URL } from "@/utils/externalPages";
import { usePageTitle } from "@/utils/title";
import { getHomeUrl } from "@/utils/urls";

export const Homepage = ({ funnel }: { funnel: Funnel }) => {
  const { t } = useI18nContext();
  const [block, cn] = useModuleBEM(styles, "Homepage");
  usePageTitle("Home");
  const enableMaintenanceBanner = useExperiment("EnableMaintenanceBanner");

  const handleSnapToSection = (
    event: React.MouseEvent<HTMLAnchorElement, MouseEvent>
  ) => {
    event.preventDefault();
    const pageHeader = document.getElementById(CoreUiComponents.PageHeader);
    const pageHeaderHeight = pageHeader ? pageHeader.offsetHeight : 0;
    const targetId = event.currentTarget.getAttribute("href")?.substring(1);
    if (targetId) {
      const element = document.getElementById(targetId);
      if (element) {
        const elementPosition =
          element.getBoundingClientRect().top + window.pageYOffset;
        window.scrollTo({
          top: elementPosition - pageHeaderHeight,
          behavior: "smooth",
        });
      }
    }
  };

  const showCarsSearch = useShowCarsSearch();
  const carsSectionBanners = useCarsSectionBanners();
  // Hide the cars deal-page section when embedded search is enabled to funnel users through it instead
  const hasCarsSection =
    !showCarsSearch &&
    (carsSectionBanners.mainSection.length > 0 ||
      carsSectionBanners.carouselSection.length > 0);
  const hasExploreFurtherSection = useExploreFurtherBanners().length > 0;

  return (
    <div className={block}>
      <Page
        className={cn("page")}
        fullWidth={true}
        header={<PageHeader contentClassName={cn("page-header")} />}
        trackingName={TrackedLoadedPageName.Home}
      >
        <div className={cn("container")}>
          <SignInBanner />
          {enableMaintenanceBanner ? (
            <div className={cn("maintenance-banner")}>
              <Icon
                className={cn("maintenance-banner-icon")}
                iconName={IconNameEnum.warning}
              />
              {t("home.maintenanceBanner.message")}
            </div>
          ) : (
            <div className={cn("main-content-container")}>
              <div className={cn("main-content")}>
                <div className={cn("header-content")}>
                  <div className={cn("go-back")}>
                    <Icon iconName={IconNameEnum.backIcon} />
                    <a href={getHomeUrl()}>{t("home.backToPerkspot")}</a>
                  </div>
                  {hasCarsSection && (
                    <a
                      href={`#${HOMEPAGE_SNAP_SECTIONS.CARS}`}
                      onClick={handleSnapToSection}
                      className={cn("header-link")}
                    >
                      {t("home.headerLink.carRentals")}
                    </a>
                  )}
                  {hasExploreFurtherSection && (
                    <a
                      href={`#${HOMEPAGE_SNAP_SECTIONS.EXPLORE_FURTHER}`}
                      onClick={handleSnapToSection}
                      className={cn("header-link")}
                    >
                      {t("home.headerLink.exploreFurther")}
                    </a>
                  )}
                </div>
                <div className={cn("body-content")}>
                  <div className={cn("title-container")}>
                    <div>
                      <h1 className={cn("title")}>
                        <Trans
                          i18nKey={"home.title"}
                          components={{
                            subSection: (
                              <span className={cn("title-subsection")} />
                            ),
                          }}
                        />
                      </h1>

                      <p className={cn("subtitle")}>{t("home.subtitle")}</p>
                    </div>
                  </div>
                  <div className={cn("search-bar-container")}>
                    <CombinedSearchBar />
                  </div>
                </div>
              </div>
            </div>
          )}
          <HomepagePerksSection />
        </div>
        <UpcomingTripsSection />
        <RecentSearchesSection />
        {hasCarsSection && <CarsSection />}
        <ExploreFurtherSection />
        <CompleteYourTripSection />
        <MoreTravelSavingsSection />
        <PopularCategoriesSection />
        <Section className={cn("got-questions-section")}>
          <div className={cn("got-questions-section-content")}>
            <SectionTitle className={cn("got-questions-section-title")}>
              {t("home.gotQuestions.title")}
            </SectionTitle>
            <ActionButton
              message={
                <Link
                  to={FAQ_URL}
                  target="_blank"
                  rel="noopener"
                  referrerpolicy="no-referrer-when-downgrade"
                >
                  {t("home.gotQuestions.buttonText")}
                </Link>
              }
              defaultStyle="outlined"
              className={cn("got-questions-section-button")}
            />
          </div>
        </Section>
      </Page>
    </div>
  );
};

export default Homepage;
